import React from 'react';
import { Routes, Route } from 'react-router-dom';
import PermissionProtectedRoute from '../auth/PermissionProtectedRoute';
import CampaignList from './CampaignList';
import CampaignForm from './CampaignForm';
import CouponList from './CouponList';
import SmsSendPage from '../../pages/SmsSendPage';
import SmsHistoryPage from '../../pages/SmsHistoryPage';
import SmsProvidersPage from '../../pages/SmsProvidersPage';
import WhatsAppProvidersPage from '../../pages/WhatsAppProvidersPage';
import WhatsAppSendPage from '../../pages/WhatsAppSendPage';
import WhatsAppHistoryPage from '../../pages/WhatsAppHistoryPage';
import WhatsAppAnalyticsPage from '../../pages/WhatsAppAnalyticsPage';
import WhatsAppCampaignsPage from '../../pages/WhatsAppCampaignsPage';
import CouponStoragePage from '../../pages/CouponStoragePage';
import { Box, Typography, Breadcrumbs, Link } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import CampaignIcon from '@mui/icons-material/Campaign';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SmsIcon from '@mui/icons-material/Sms';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptIcon from '@mui/icons-material/Receipt';

// مكون للخبز (Breadcrumbs)
const MarketingBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(x => x);

  // تحديد عنوان الصفحة والفتات الخبز حسب المسار
  let pageTitle = "التسويق";
  let breadcrumbs = [
    <Link
      key="home"
      component={RouterLink}
      to="/"
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
      الرئيسية
    </Link>,
    <Link
      key="marketing"
      component={RouterLink}
      to="/marketing"
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <CampaignIcon sx={{ mr: 0.5 }} fontSize="inherit" />
      التسويق
    </Link>
  ];

  if (pathnames.includes('coupon-storage')) {
    pageTitle = "تتبع أكواد الكوبونات";
    breadcrumbs.push(
      <Typography key="coupon-storage" color="text.primary">
        تتبع الكوبونات
      </Typography>
    );
  } else if (pathnames.includes('campaigns')) {
    if (pathnames.includes('new')) {
      pageTitle = "إنشاء حملة تسويقية جديدة";
      breadcrumbs.push(
        <Link
          key="campaigns"
          component={RouterLink}
          to="/marketing/campaigns"
        >
          الحملات التسويقية
        </Link>,
        <Typography key="new" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <AddIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          إنشاء حملة جديدة
        </Typography>
      );
    } else if (pathnames.includes('edit')) {
      pageTitle = "تعديل حملة تسويقية";
      breadcrumbs.push(
        <Link
          key="campaigns"
          component={RouterLink}
          to="/marketing/campaigns"
        >
          الحملات التسويقية
        </Link>,
        <Typography key="edit" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <EditIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          تعديل حملة
        </Typography>
      );
    } else {
      pageTitle = "الحملات التسويقية";
      breadcrumbs.push(
        <Typography key="campaigns" color="text.primary">
          الحملات التسويقية
        </Typography>
      );
    }
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom align="right">
        {pageTitle}
      </Typography>
      <Breadcrumbs separator="›" aria-label="breadcrumb" dir="rtl">
        {breadcrumbs}
      </Breadcrumbs>
    </Box>
  );
};

// مكون الصفحة الرئيسية للتسويق
const MarketingDashboard: React.FC = () => {
  return (
    <Box>
      <MarketingBreadcrumbs />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', p: 3 }}>
        <Typography variant="h6" align="center">
          مرحبًا بك في قسم التسويق
        </Typography>
        <Typography align="center">
          من هنا يمكنك إدارة الحملات التسويقية والكوبونات وعروض المنتجات
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Link component={RouterLink} to="/marketing/campaigns" underline="none">
            <Box
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                p: 3,
                borderRadius: 2,
                textAlign: 'center',
                width: 200,
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              <CampaignIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6">
                الحملات التسويقية
              </Typography>
            </Box>
          </Link>
          <Link component={RouterLink} to="/marketing/campaigns/new" underline="none">
            <Box
              sx={{
                bgcolor: 'secondary.main',
                color: 'white',
                p: 3,
                borderRadius: 2,
                textAlign: 'center',
                width: 200,
                '&:hover': {
                  bgcolor: 'secondary.dark',
                }
              }}
            >
              <AddIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6">
                إنشاء حملة جديدة
              </Typography>
            </Box>
          </Link>
          <Link component={RouterLink} to="/marketing/coupon-storage" underline="none">
            <Box
              sx={{
                bgcolor: 'info.main',
                color: 'white',
                p: 3,
                borderRadius: 2,
                textAlign: 'center',
                width: 200,
                '&:hover': {
                  bgcolor: 'info.dark',
                }
              }}
            >
              <ReceiptIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6">
                تتبع الكوبونات
              </Typography>
            </Box>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

// مكون مسارات التسويق
const MarketingRoutes: React.FC = () => {
  return (
    <Routes>
      <Route index element={
        <PermissionProtectedRoute sectionId="marketing" pageId="dashboard" actionId="view_page">
          <MarketingDashboard />
        </PermissionProtectedRoute>
      } />
      <Route path="campaigns" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="campaigns" actionId="view_page">
          <CampaignList />
        </PermissionProtectedRoute>
      } />
      <Route path="campaigns/new" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="new-campaign" actionId="view_page">
          <CampaignForm />
        </PermissionProtectedRoute>
      } />
      <Route path="campaigns/:id/edit" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="campaigns" actionId="edit_campaign">
          <CampaignForm isEdit={true} />
        </PermissionProtectedRoute>
      } />
      <Route path="campaigns/:id/view" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="campaigns" actionId="view_campaign">
          <CampaignForm isEdit={true} />
        </PermissionProtectedRoute>
      } />
       <Route path="coupons" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="coupons" actionId="view_page">
          <CouponList />
        </PermissionProtectedRoute>
      } />
      <Route path="coupon-storage" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="coupon-storage" actionId="view_page">
          <CouponStoragePage />
        </PermissionProtectedRoute>
      } />
      <Route path="sms/send" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="sms-send" actionId="view_page">
          <SmsSendPage />
        </PermissionProtectedRoute>
      } />
      <Route path="sms/history" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="sms-history" actionId="view_page">
          <SmsHistoryPage />
        </PermissionProtectedRoute>
      } />
      <Route path="sms/providers" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="sms-providers" actionId="view_page">
          <SmsProvidersPage />
        </PermissionProtectedRoute>
      } />
      <Route path="whatsapp/providers" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="whatsapp-providers" actionId="view_page">
          <WhatsAppProvidersPage />
        </PermissionProtectedRoute>
      } />
      <Route path="whatsapp/send" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="whatsapp-send" actionId="view_page">
          <WhatsAppSendPage />
        </PermissionProtectedRoute>
      } />
      <Route path="whatsapp/campaigns" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="whatsapp-campaigns" actionId="view_page">
          <WhatsAppCampaignsPage />
        </PermissionProtectedRoute>
      } />
      <Route path="whatsapp/history" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="whatsapp-history" actionId="view_page">
          <WhatsAppHistoryPage />
        </PermissionProtectedRoute>
      } />
      <Route path="whatsapp/analytics" element={
        <PermissionProtectedRoute sectionId="marketing" pageId="whatsapp-analytics" actionId="view_page">
          <WhatsAppAnalyticsPage />
        </PermissionProtectedRoute>
      } />
    </Routes>
  );
};

export default MarketingRoutes;